import {Feature, startModalProcessing, stopModalLoading} from '../../reducers/environmentReducer';
import {type GatewayConfiguration} from '../../models/GatewayConfiguration';
import {type Transaction, type OrderService} from '../order';
import {store} from '../../store';
import {setupPeachpayButton} from './button';
import {PaymentConfiguration, registerGatewayBatch} from '../../reducers/paymentConfigurationReducer';
import {getPurchaseOrderMethodConfiguration, initPurchaseOrderSupport} from './purchaseOrder';
import {getFreeMethodConfiguration, initFreeOrderSupport} from '../free/free';
import {getCODMethodConfiguration, initCODSupport} from './cod';
import {getChequeMethodConfiguration, initChequeSupport} from './cheque';
import {getBacsMethodConfiguration, initBacsSupport} from './bacs';
import {$qsAll} from '../../../../@shared/ts/dom';
import {FeatureFlag} from '../../../../@type/features';

/**
 * Here is where peachpay provided payment methods hook into the shared button.
 */
const buttonListeners: Record<string, (event: MouseEvent) => void> = {};

export function initPeachPayMethods(orderService: OrderService) {
	setupPeachpayButton();

	// Important that methods are registered before init
	registerGateways();

	// Initialize the methods, these are expected to handle their feature flags on their own.
	initPurchaseOrderSupport(orderService);
	initCODSupport(orderService);
	initChequeSupport(orderService);
	initBacsSupport(orderService);
	initFreeOrderSupport(orderService);

	$qsAll<HTMLElement>('.peachpay-integrated-btn', $el => {
		$el.addEventListener('click', (event: MouseEvent) => {
			const $target = event.target as HTMLElement | null;
			const $button = $target?.closest<HTMLButtonElement>('button');
			if (!$button) {
				return;
			}

			// Run the registered listener for the appropriate payment method
			const listener = buttonListeners[PaymentConfiguration.selectedGateway()];
			if (listener) {
				listener(event);
			}
		});
	});
}

export function addButtonListener(gatewayId: string, listener: (event: MouseEvent) => void) {
	buttonListeners[gatewayId] = listener;
}

function registerGateways() {
	const gatewayConfigurations: Record<string, GatewayConfiguration> = {};

	if (Feature.enabled(FeatureFlag.PEACHPAY_PURCHASE_ORDER_GATEWAY)) {
		const config = getPurchaseOrderMethodConfiguration();
		gatewayConfigurations[config.gatewayId] = config;
	}

	if (Feature.enabled(FeatureFlag.WOOCOMMERCE_COD_GATEWAY)) {
		const config = getCODMethodConfiguration();
		gatewayConfigurations[config.gatewayId] = config;
	}

	if (Feature.enabled(FeatureFlag.WOOCOMMERCE_CHEQUE_GATEWAY)) {
		const config = getChequeMethodConfiguration();
		gatewayConfigurations[config.gatewayId] = config;
	}

	if (Feature.enabled(FeatureFlag.WOOCOMMERCE_BACS_GATEWAY)) {
		const config = getBacsMethodConfiguration();
		gatewayConfigurations[config.gatewayId] = config;
	}

	const freeConfig = getFreeMethodConfiguration();
	gatewayConfigurations[freeConfig.gatewayId] = freeConfig;

	store.dispatch(registerGatewayBatch(gatewayConfigurations));
}

export async function defaultOrderFlow(orderService: OrderService, transaction: Transaction, extraFormData: Record<string, string> = {}) {
	store.dispatch(startModalProcessing());

	const {error: orderError, result: orderResult} = await orderService.placeOrder(transaction, extraFormData);
	if (orderError || !orderResult || orderResult.result !== 'success') {
		store.dispatch(stopModalLoading());
		return;
	}

	if (window.top) {
		await transaction.complete({
			paymentStatus: 'on-hold',
			orderStatus: 'on-hold',
		});

		window.top.location = orderResult.redirect;
	}
}
